import { Request } from '@konv/http'
import { UserStorage } from '../shared/tools/UserStorage';

export class IntegrationsAPI {

    static async request({ endpoint = '/', method = 'GET', headers, query, data }) {
        return await Request.request({
            endpoint: endpoint,
            method: method,
            headers: headers,
            service: 'integrations',
            ignoreVersion: true,
            params: query,
            data: data
        });
    }

    static async requestAuthenticated({ endpoint = '/', method = 'GET', query, data }) {
        return await IntegrationsAPI.request({
            endpoint,
            headers: { 'Authorization': 'Bearer ' + UserStorage.getAccessToken() },
            method,
            query,
            data,
        });
    }

    static async getConfigurationsByAlias(environmentId, alias) {
        return (await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/configurations/${alias}`,
            method: 'GET',
        })).getData({});
    }

    static async postSyncTablesByEcletica(environmentId){
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/ecletica/tables/sync`,
            method: 'POST',
        });
    }

    static async postSyncCatalogEcletica(environmentId){
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/ecletica/catalog/sync`,
            method: 'POST',
        });
    }

    static async putConfigurationsByAlias(environmentId, alias, data) {
        return await IntegrationsAPI.request({
            method: 'PUT',
            endpoint: `/${environmentId}/configurations/${alias}`,
            headers: { 'Authorization': 'Bearer ' + UserStorage.getAccessToken() },
            data,
        });
    }

    static async getGoogleKeys(environmentId) {
        return (await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/google`,
            method: 'GET',
        })).getData({});
    }

    static async setGoogleTagManager(environmentId, gtmId) {
        return (await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/google/tag-manager`,
            method: 'PUT',
            data: { tag_manager: gtmId }
        })).getData({});
    }

    static async setGoogleAnalytics(environmentId, analytics) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/google/analytics`,
            method: 'PUT',
            data: { analytics: analytics }
        });
    }

    static async getIFoodAccount(environmentId) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/ifood/account`,
            method: 'GET',
        });
    }

    static async setIFoodAccount({ environmentId, ...configs }) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/ifood/account`,
            method: 'POST',
            data: configs
        });
    }

    static async getBeeDeliveryAccount(environmentId) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/bee_delivery/account`,
            method: 'GET',
        });
    }

    static async upsertBeeDeliveryAccount(environmentId, configs) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/bee_delivery/account`,
            method: 'POST',
            data: configs
        });
    }

    static async getSodeAccount(environmentId) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/sode/account`,
            method: 'GET',
        });
    }

    static async upsertSodeAccount(environmentId, configs) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/sode/account`,
            method: 'POST',
            data: configs
        });
    }


	static async getOrderCancellationCode (orderId, environmentId) {
		return await IntegrationsAPI.requestAuthenticated({
			endpoint: `/shipping/${environmentId}/orders/${orderId}/cancellation-code`,
			headers: { 'Authorization': 'Bearer ' + UserStorage.getAccessToken() },
		});
	}
    
    static async getMottuAccount(environmentId) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/mottu/account`,
            method: 'GET',
        });
    }

    static async createMottuAccount(environmentId, active, storeId, pickupAddress, showTrackingUrl, cancelationControl, allowPreview, allowOfflinePayment, apiToken) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/mottu/account`,
            method: 'POST',
            data: {
                active,
                storeId,
                pickupAddress,
                showTrackingUrl,
                cancelationControl,
                allowPreview,
                allowOfflinePayment,
                apiToken
            }
        });
    }

    static async setMottuAccount(environmentId, active, storeId, pickupAddress, showTrackingUrl, cancelationControl, allowPreview, allowOfflinePayment, apiToken) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/mottu/account`,
            method: 'PUT',
            data: {
                active,
                storeId,
                pickupAddress,
                showTrackingUrl,
                cancelationControl,
                allowPreview,
                allowOfflinePayment,
                apiToken
            }
        });
    }


    static async getFoodDeliveryAccount(environmentId) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/foodydelivery/account`,
            method: 'GET',
        });
    }

    static async setFoodDeliveryAccount({ environmentId, ...fields}) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/foodydelivery/account`,
            method: 'POST',
            data: fields
        });
    }

	static async cancelOrder (orderId, reason, cancellationCode, environmentId) {
		return await IntegrationsAPI.requestAuthenticated({
			endpoint: `/shipping/${environmentId}/orders/${orderId}/cancel`,
            method: 'POST',
			data: {
				reason, 
				cancellationCode
			}
		});
	}

    
    static async getOpenDeliveryCredentials(environmentId) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/open-delivery/${environmentId}/credentials`,
            method: 'GET',
        });
    }

    static async putOpenDeliveryCredentials(environmentId, data) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/open-delivery/${environmentId}/credentials`,
            method: 'PUT',
            data: data
        });
    }

    static async getAtenaAIConfigurations (environmentId) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/atenaai/configurations`,
            method: 'GET',
        });
    }

    static async getAtenaAIStores (environmentId) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/atenaai/stores`,
            method: 'GET',
        });
    }

    static async getAtenaAICatalogs (environmentId) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/atenaai/catalogs`,
            method: 'GET',
        });
    }

    static async getAtenaAICheckouts (environmentId) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/atenaai/checkouts`,
            method: 'GET',
        });
    }

    static async getAtenaAIPayments (environmentId) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/atenaai/payments`,
            method: 'GET',
        });
    }

    static async upsertAtenaAIConfigurations (environmentId, configurations) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/atenaai/configurations`,
            method: 'PUT',
            data: configurations || {}
        });
    }

    static async getBlingConfigurations (environmentId) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/bling/configurations`,
            method: 'GET',
        });
    }

    static async upsertBlingConfigurations (environmentId, configurations) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/bling/configurations`,
            method: 'PUT',
            data: configurations || {}
        });
    }

    static async getBlingAccount (environmentId) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/bling/account`,
            method: 'GET',
        });
    }

    static async linkBlingAccount ({ environmentId, code, clientId, clientSecret }) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/bling/application`,
            method: 'PUT',
            data: {
                code,
                clientId,
                clientSecret
            }
        });
    }

    static async getBlingPaymentMethods (environmentId) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/bling/payment-methods`,
            method: 'GET',
        });
    }

    static async getBlingStatuses (environmentId) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/bling/statuses`,
            method: 'GET',
        });
    }

    static async getBlingSellers (environmentId) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/bling/sellers`,
            method: 'GET',
        });
    }

    static async getBlingFinanceCategories (environmentId) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/bling/finance-categories`,
            method: 'GET',
        });
    }


    static async getApplePayMerchant(environmentId) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/applepay/merchant`,
            method: 'GET',
        });
    }

    static async upsertApplePayMerchant(environmentId, merchant) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/applepay/merchant`,
            method: 'PUT',
            data: merchant
        });
    }

    static async getApplePayDomains(environmentId) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/applepay/domains`,
            method: 'GET',
        });
    }

    static async upsertApplePayDomain(environmentId, { domain, file }) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/applepay/domains`,
            method: 'PUT',
            data: {
                domain,
                file
            }
        });
    }

    static async getApplePayCertificate(environmentId) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/applepay/certificate`,
            method: 'GET',
        });
    }

    static async upsertApplePayCertificate(environmentId, { file }) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/applepay/certificate`,
            method: 'PUT',
            data: {
                file
            }
        });
    }
    
    static async deleteApplePayCertificate(environmentId) {
        return await IntegrationsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/applepay/certificate`,
            method: 'DELETE',
        });
    }
}